// @ts-strict-ignore
import zip from 'lodash/zip';

import {DATASET_MIXPANEL, PropertyType} from 'common/report/constants';
import {FilterClause, GroupClause, InsightsQueryResults} from 'common/report/insights/util/types';
import {InsightsBookmarkParams, InsightsResourceType} from 'common/types/reports/bookmark';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {queryModule} from 'common/report/report-manager/query';
import {flattenSeries} from 'common/report/insights/util';
import {MPProperties} from 'common/constants';
import {getLocation} from 'common/util/url';
import {BlockedTimeWindow} from 'common/constants';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {INSIGHTS_DATE_RANGE_TYPES} from 'common/widgets/property-filter-menu/models/insights-filter';

export const DEFAULT_NUM_REPLAYS = 100;
export const REPLAY_ID_PROPERTY = `$mp_replay_id`;

/**
 * returns date in format "YYYY-MM-DDT00:00:00" since that is what query api needs
 * @param blockedReplayFrame
 * @returns
 */
function getDateFilterValue(blockedReplayFrame: BlockedTimeWindow): [string, string] {
  return [
    new Date(blockedReplayFrame.start * 1000).toISOString().slice(0, 19),
    new Date(blockedReplayFrame.end * 1000).toISOString().slice(0, 19),
  ];
}

function getDistinctIdFilter(distinctIds: string[]): FilterClause {
  return {
    dataset: `$mixpanel`,
    value: `$distinct_id`,
    // use the events resource type, otherwise we don't include anon users.
    resourceType: InsightsResourceType.Events,
    profileType: null,
    search: ``,
    dataGroupId: null,
    filterType: PropertyType.String,
    defaultType: PropertyType.String,
    filterOperator: StringOperator.Equals,
    filterValue: distinctIds,
    propertyObjectKey: null,
  };
}

/**
 * Entrypoint to make any session replay related query to ARB, including
 * - segmenting an insights report to get replay IDs for event playback
 * - fetching events that occurred within a replay
 * - fetching metadata about replays
 */
async function makeSessionReplayQuery({
  params,
  blockedReplayFrames,
  limit = DEFAULT_NUM_REPLAYS,
  ignoreSampling = false,
  queryOrigin = `session-replay`,
}: {
  params: InsightsBookmarkParams;
  blockedReplayFrames: BlockedTimeWindow[];
  limit?: number;
  ignoreSampling?: boolean;
  queryOrigin?: string;
}): Promise<InsightsQueryResults> {
  const blockedReplayFramesWithUsageGreaterThan1 = blockedReplayFrames.filter(
    (blockedReplayFrame) => blockedReplayFrame.usage_pct > 1,
  );
  const timeFiltersForBlockedReplays: FilterClause[] = blockedReplayFramesWithUsageGreaterThan1.map(
    (blockedReplayFrame) => ({
      dataset: DATASET_MIXPANEL,
      value: MPProperties.$Time,
      resourceType: InsightsResourceType.Events,
      profileType: null,
      search: ``,
      dataGroupId: null,
      filterType: PropertyType.Datetime,
      defaultType: PropertyType.Datetime,
      filterOperator: INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_BETWEEN as unknown as any,
      filterValue: getDateFilterValue(blockedReplayFrame),
      propertyObjectKey: null,
    }),
  );

  params.sections.filter.push(...timeFiltersForBlockedReplays);
  const body = {
    bookmark: params,
    use_query_cache: true,
    limit,
    ignore_sampling: ignoreSampling,
  };

  const queryParams = {
    ...(getWorkspaceId() ? {workspace_id: getWorkspaceId()} : {}),
    project_id: getProjectId(),
    query_origin: queryOrigin,
  };

  const response = await queryModule.executeReportQuery({
    endpoint: `insights`,
    body,
    queryParams,
    location: {
      search: getLocation().search,
      hash: getLocation().hash,
      pathname: getLocation().pathname,
      key: ``,
      state: ``,
    },
  });
  // TODO: handle error
  const results: InsightsQueryResults = await response.json();
  return results;
}

function getLabeledSegmentValues(segmentValues: string[], groups: GroupClause[]): Record<string, string> {
  // ignore show clause
  return zip(segmentValues.slice(1), groups).reduce((acc, [segment, group]) => {
    acc[group.value as string] = segment;
    return acc;
  }, {});
}

function flattenSeriesWithoutMetadata(
  results: InsightsQueryResults,
  groups: GroupClause[],
): Array<[string[], Record<string, number>]> {
  return flattenSeries(results.series).filter(([path]) => {
    // skip all the "overall" segments
    return path.length === groups.length + 1 && path[groups.length] !== `$overall`;
  });
}

// export for mockable utils
export const queryUtils = {
  getDistinctIdFilter,
  getLabeledSegmentValues,
  flattenSeriesWithoutMetadata,
  makeSessionReplayQuery,
};
